import React, { useEffect, useState } from "react";
import { Box, Skeleton } from "@mui/material";
import { DetailCard } from "../../../shared/cards/DetailCard";
import { BodyText } from "../../../shared/text/BodyText.styled";
import { PrimaryButton } from "../../../shared/buttons/PrimaryButton.styled";
import AddIcon from "@mui/icons-material/Add";
import EmptyStateCard from "../../../shared/cards/EmptyStateCard";
import { ListTitle } from "../../../shared/inputs/SelectList.styled";
import { useProject } from "../../../context/ProjectContext";
import { useBanner } from "../../../context/BannerContext";
import { useDatasources } from "../../../context/DatasourceContext";
import { useDrawerActions } from "../../../context/DrawerContext";
import { useContractingLoader } from "../../../hooks/useContractingLoader";
import { fetchBidGroupsPopulatedUncached } from "../../../../api/bids";
import dayjs from "dayjs";
import { formatCurrency } from "../format";
import { maxServerUpdatedAt } from "../../../../excel/datasources/staleness";
import { BidGroup } from "../../../../interfaces/BidTabs";
import { BID_PACKAGES_LOAD_ERROR } from "../../../constants";

interface ContractingSummary {
  projectedBuy: number;
  budget: number;
  buyValue: number;
  packageCount: number;
  awardedCount: number;
  lastUpdated: string;
}

const summarize = (bidGroups: BidGroup[]): ContractingSummary => ({
  projectedBuy: bidGroups.reduce((sum, bg) => sum + (bg.projected_buy ?? 0), 0),
  budget: bidGroups.reduce((sum, bg) => sum + (bg.budget ?? 0), 0),
  buyValue: bidGroups.reduce((sum, bg) => sum + (bg.buy_value ?? 0), 0),
  packageCount: bidGroups.length,
  awardedCount: bidGroups.filter((bg) => bg.award).length,
  lastUpdated: maxServerUpdatedAt(bidGroups),
});

const ContractingPreview: React.FC = () => {
  const { selectedProject } = useProject();
  const { addDatasource } = useDatasources();
  const { closeDrawer } = useDrawerActions();
  const { loadContracting } = useContractingLoader();
  const { showBanner } = useBanner();
  const [summary, setSummary] = useState<ContractingSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const projectId = selectedProject?.id;

  useEffect(() => {
    if (!projectId) return;

    let isCurrent = true;

    const loadBidGroups = async () => {
      setLoading(true);
      try {
        const result = await fetchBidGroupsPopulatedUncached(projectId);
        if (!result.success) throw result.error;
        if (isCurrent) setSummary(summarize(result.data?.bidGroups ?? []));
      } catch (error) {
        if (isCurrent) showBanner(BID_PACKAGES_LOAD_ERROR, "error");
      } finally {
        if (isCurrent) setLoading(false);
      }
    };

    loadBidGroups();

    return () => {
      isCurrent = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // See EstimatesList: the load decides whether a card is earned, and the loader owns the selection.
  const handleAdd = async () => {
    if (!projectId) return;
    const { dataLoaded } = await loadContracting();
    if (!dataLoaded) return;
    addDatasource("ediphi", "contracting");
    closeDrawer();
  };

  // Titled like the lists rather than left bare: their title sits above the empty card too, and this
  // one's normally lives inside the DetailCard the early returns skip.
  if (loading) {
    return (
      <Box>
        <ListTitle>Contracting</ListTitle>
        <Skeleton variant="rounded" height={220} sx={{ borderRadius: "8px" }} />
      </Box>
    );
  }
  if (!summary || summary.packageCount === 0) {
    return (
      <Box>
        <ListTitle>Contracting</ListTitle>
        <EmptyStateCard text="This project has no bid packages." />
      </Box>
    );
  }

  const details = [
    { label: "Projected Buy", value: formatCurrency(summary.projectedBuy) },
    { label: "Budget", value: formatCurrency(summary.budget) },
    { label: "Buy Value", value: formatCurrency(summary.buyValue) },
    { label: "Bid Packages", value: String(summary.packageCount) },
    { label: "Awarded", value: `${summary.awardedCount} of ${summary.packageCount}` },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <DetailCard
        outlined
        title="Contracting"
        footer={<BodyText>{`Last Updated: ${dayjs(summary.lastUpdated).format("MM/DD/YYYY HH:mm")}`}</BodyText>}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {details.map(({ label, value }) => (
            <Box key={label} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <BodyText>{label}</BodyText>
              <BodyText>{value}</BodyText>
            </Box>
          ))}
        </Box>
      </DetailCard>
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <PrimaryButton onClick={() => void handleAdd()} startIcon={<AddIcon />}>
          Add
        </PrimaryButton>
      </Box>
    </Box>
  );
};

export default ContractingPreview;
